import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  PaintBrushIcon,
  MusicalNoteIcon,
  VideoCameraIcon,
  BookOpenIcon,
  MicrophoneIcon,
  PuzzlePieceIcon,
  CameraIcon,
  AcademicCapIcon,
  ComputerDesktopIcon,
  HeartIcon,
  SparklesIcon,
  FolderIcon,
  UserGroupIcon,
  DocumentTextIcon,
  FireIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import PatreonNav from '../../components/PatreonNav';
import PatreonFooter from '../../components/PatreonFooter';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

interface Creator {
  id: string;
  username: string;
  displayName?: string;
  bio?: string;
  avatar?: string;
  category?: string;
  patronCount?: number;
  postCount?: number;
}

interface CategoryInfo {
  name: string;
  slug: string;
  description: string;
  icon: React.ElementType;
  color: string;
  gradient: string;
  stats: {
    creators: string;
    patrons: string;
    earnings: string;
  };
  features: string[];
}

const categories: Record<string, CategoryInfo> = {
  art: {
    name: 'Art',
    slug: 'art',
    description: 'Support talented artists creating illustrations, paintings, digital art, and more',
    icon: PaintBrushIcon,
    color: 'purple',
    gradient: 'from-purple-600 to-pink-600',
    stats: { creators: '125K', patrons: '2.1M', earnings: '$45M' },
    features: [
      'Exclusive artwork and process videos',
      'Early access to new pieces',
      'Commission opportunities',
      'Art tutorials and techniques',
    ],
  },
  music: {
    name: 'Music',
    slug: 'music',
    description: 'Discover and support musicians, composers, and audio creators',
    icon: MusicalNoteIcon,
    color: 'blue',
    gradient: 'from-blue-600 to-cyan-600',
    stats: { creators: '98K', patrons: '1.8M', earnings: '$38M' },
    features: [
      'Early access to new releases',
      'Behind-the-scenes content',
      'Exclusive tracks and demos',
      'Music production tutorials',
    ],
  },
  video: {
    name: 'Video',
    slug: 'video',
    description: 'Support video creators making entertainment, education, and documentary content',
    icon: VideoCameraIcon,
    color: 'red',
    gradient: 'from-red-600 to-orange-600',
    stats: { creators: '142K', patrons: '3.2M', earnings: '$68M' },
    features: [
      'Ad-free viewing experience',
      'Extended and bonus content',
      'Behind-the-scenes footage',
      'Early video releases',
    ],
  },
  writing: {
    name: 'Writing',
    slug: 'writing',
    description: 'Support writers creating novels, poetry, journalism, and creative content',
    icon: BookOpenIcon,
    color: 'amber',
    gradient: 'from-amber-600 to-yellow-600',
    stats: { creators: '87K', patrons: '1.5M', earnings: '$32M' },
    features: [
      'Exclusive chapters and stories',
      'Early access to new releases',
      'Writing workshops and tips',
      'Character development insights',
    ],
  },
  podcasts: {
    name: 'Podcasts',
    slug: 'podcasts',
    description: 'Support podcasters creating compelling audio content and discussions',
    icon: MicrophoneIcon,
    color: 'green',
    gradient: 'from-green-600 to-emerald-600',
    stats: { creators: '76K', patrons: '1.3M', earnings: '$28M' },
    features: [
      'Ad-free episodes',
      'Bonus episodes and content',
      'Early access to new episodes',
      'Behind-the-scenes discussions',
    ],
  },
  gaming: {
    name: 'Gaming',
    slug: 'gaming',
    description: 'Support game developers, streamers, and gaming content creators',
    icon: PuzzlePieceIcon,
    color: 'indigo',
    gradient: 'from-indigo-600 to-purple-600',
    stats: { creators: '164K', patrons: '4.1M', earnings: '$92M' },
    features: [
      'Early access to games',
      'Exclusive gaming content',
      'Development updates',
      'Beta testing opportunities',
    ],
  },
  photography: {
    name: 'Photography',
    slug: 'photography',
    description: 'Support photographers capturing stunning images and sharing their craft',
    icon: CameraIcon,
    color: 'pink',
    gradient: 'from-pink-600 to-rose-600',
    stats: { creators: '54K', patrons: '892K', earnings: '$19M' },
    features: [
      'High-resolution images',
      'Photography tutorials',
      'Editing presets and tools',
      'Behind-the-scenes shoots',
    ],
  },
  education: {
    name: 'Education',
    slug: 'education',
    description: 'Support educators creating courses, tutorials, and educational content',
    icon: AcademicCapIcon,
    color: 'teal',
    gradient: 'from-teal-600 to-cyan-600',
    stats: { creators: '109K', patrons: '2.4M', earnings: '$52M' },
    features: [
      'Comprehensive course materials',
      'Interactive learning content',
      'Q&A sessions with experts',
      'Certificates and resources',
    ],
  },
  technology: {
    name: 'Technology',
    slug: 'technology',
    description: 'Support tech creators making software, hardware, and innovation content',
    icon: ComputerDesktopIcon,
    color: 'slate',
    gradient: 'from-slate-600 to-gray-600',
    stats: { creators: '92K', patrons: '1.9M', earnings: '$41M' },
    features: [
      'Open-source projects',
      'Tech tutorials and guides',
      'Product reviews and analysis',
      'Development resources',
    ],
  },
  other: {
    name: 'Other',
    slug: 'other',
    description: 'Explore creators in various unique and creative categories',
    icon: FolderIcon,
    color: 'gray',
    gradient: 'from-gray-600 to-slate-600',
    stats: { creators: '203K', patrons: '3.6M', earnings: '$78M' },
    features: [
      'Diverse creative content',
      'Unique projects and ideas',
      'Cross-category collaborations',
      'Innovative creations',
    ],
  },
};

export default function CategoryLandingPage() {
  const { category: categorySlug } = useParams<{ category: string }>();
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<'popular' | 'newest' | 'az'>('popular');

  const categoryInfo = categorySlug
    ? categories[categorySlug.toLowerCase()]
    : undefined;

  useEffect(() => {
    if (categoryInfo) {
      fetchCreators();
    }
  }, [categoryInfo, sortBy]);

  const fetchCreators = async () => {
    if (!categoryInfo) return;

    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/creators/search`, {
        params: {
          category: categoryInfo.name,
          sort: sortBy,
          limit: 12,
        },
      });

      if (response.data.creators) {
        setCreators(response.data.creators);
      } else if (Array.isArray(response.data)) {
        setCreators(response.data);
      }
    } catch (error) {
      console.error('Error fetching creators:', error);
      setCreators([]);
    } finally {
      setLoading(false);
    }
  };

  if (!categoryInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Category Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            The category you're looking for doesn't exist.
          </p>
          <Link
            to="/explore"
            className="text-purple-600 hover:text-purple-700 font-medium"
          >
            ← Back to Explore
          </Link>
        </div>
      </div>
    );
  }

  const Icon = categoryInfo.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Global Navigation */}
      <PatreonNav />
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${categoryInfo.gradient} text-white py-20`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Icon className="h-20 w-20 mx-auto mb-6" />
            <h1 className="text-5xl font-bold mb-4">{categoryInfo.name}</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
              {categoryInfo.description}
            </p>

            {/* Category Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <UserGroupIcon className="h-8 w-8 mx-auto mb-2" />
                <div className="text-3xl font-bold">{categoryInfo.stats.creators}</div>
                <div className="text-sm opacity-90">Creators</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <HeartIcon className="h-8 w-8 mx-auto mb-2" />
                <div className="text-3xl font-bold">{categoryInfo.stats.patrons}</div>
                <div className="text-sm opacity-90">Patrons</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <SparklesIcon className="h-8 w-8 mx-auto mb-2" />
                <div className="text-3xl font-bold">{categoryInfo.stats.earnings}</div>
                <div className="text-sm opacity-90">Monthly Earnings</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-white rounded-lg shadow-md p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            What You'll Get
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {categoryInfo.features.map((feature, index) => (
              <div key={index} className="flex items-start">
                <div className={`flex-shrink-0 h-6 w-6 text-${categoryInfo.color}-600 mr-3`}>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <p className="text-gray-700">{feature}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Creators Section */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 flex items-center">
              <FireIcon className="h-8 w-8 text-orange-500 mr-3" />
              Top {categoryInfo.name} Creators
            </h2>
            <p className="text-gray-600 mt-2">
              Discover and support amazing creators in this category
            </p>
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600"
          >
            <option value="popular">Most Popular</option>
            <option value="newest">Newest</option>
            <option value="az">A-Z</option>
          </select>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            <p className="mt-4 text-gray-600">Loading creators...</p>
          </div>
        )}

        {/* Creators Grid */}
        {!loading && creators.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {creators.map((creator) => (
              <Link
                key={creator.id}
                to={`/${creator.username}`}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden group"
              >
                {/* Avatar Section */}
                <div className={`bg-gradient-to-br ${categoryInfo.gradient} p-8`}>
                  <img
                    src={creator.avatar || '/default-avatar.png'}
                    alt={creator.displayName || creator.username}
                    className="h-32 w-32 rounded-full mx-auto object-cover border-4 border-white shadow-lg group-hover:scale-110 transition-transform"
                  />
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
                    {creator.displayName || creator.username}
                  </h3>
                  <p className="text-center text-gray-600 text-sm mb-4">
                    @{creator.username}
                  </p>

                  {creator.bio && (
                    <p className="text-gray-600 text-sm text-center mb-4 line-clamp-2">
                      {creator.bio}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                    <div className="text-center">
                      <UserGroupIcon className={`h-5 w-5 text-${categoryInfo.color}-600 mx-auto mb-1`} />
                      <div className="text-lg font-bold text-gray-900">
                        {creator.patronCount || 0}
                      </div>
                      <div className="text-xs text-gray-600">Patrons</div>
                    </div>
                    <div className="text-center">
                      <DocumentTextIcon className={`h-5 w-5 text-${categoryInfo.color}-600 mx-auto mb-1`} />
                      <div className="text-lg font-bold text-gray-900">
                        {creator.postCount || 0}
                      </div>
                      <div className="text-xs text-gray-600">Posts</div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* No Creators State */}
        {!loading && creators.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-600 text-lg mb-4">
              No creators found in this category yet
            </p>
            <Link
              to="/explore"
              className="text-purple-600 hover:text-purple-700 font-medium"
            >
              Explore other categories →
            </Link>
          </div>
        )}

        {/* CTA Section */}
        <div className={`bg-gradient-to-r ${categoryInfo.gradient} rounded-lg shadow-xl p-12 text-center text-white`}>
          <RocketLaunchIcon className="h-16 w-16 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4">
            Ready to Join the {categoryInfo.name} Community?
          </h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Start supporting creators today and get exclusive access to amazing content
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/explore"
              className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              Browse All Creators
            </Link>
            <Link
              to="/register"
              className="bg-white/20 backdrop-blur-sm text-white border-2 border-white px-8 py-4 rounded-lg font-semibold hover:bg-white/30 transition-colors"
            >
              Become a Creator
            </Link>
          </div>
        </div>
      </div>

      {/* Related Categories */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Explore Other Categories
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {Object.values(categories)
              .filter((cat) => cat.slug !== categorySlug)
              .slice(0, 5)
              .map((cat) => {
                const CategoryIcon = cat.icon;
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="bg-gray-50 rounded-lg p-6 text-center hover:shadow-lg transition-shadow group"
                  >
                    <CategoryIcon className={`h-12 w-12 text-${cat.color}-600 mx-auto mb-3 group-hover:scale-110 transition-transform`} />
                    <h3 className="font-semibold text-gray-900">{cat.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {cat.stats.creators} creators
                    </p>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>

      {/* Global Footer */}
      <PatreonFooter />
    </div>
  );
}
